import { useMemo } from 'react';
import Reveal from '../components/Reveal';
import PartnershipApplicationForm from '../components/PartnershipApplicationForm';
import { useLanguage } from '../context/LanguageContext';
import { getYouTubeEmbedUrl } from '../utils/getYouTubeEmbedUrl';

const heroBackgroundImage =
  'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1800&q=80';

// One infographic per zigzag row, in page order. Previous photo links: content/previousImages.js
const sectionImageBase = `${process.env.PUBLIC_URL}/images/MainAndPartners`;
const sectionImages = [
  `${sectionImageBase}/partners_1.jfif`,
  `${sectionImageBase}/partners_2.jfif`,
  `${sectionImageBase}/partners_3.jfif`,
  `${sectionImageBase}/partners_4.jfif`,
  `${sectionImageBase}/partners_5.jfif`,
  `${sectionImageBase}/partners_6.jfif`,
  `${sectionImageBase}/partners_7.jfif`,
  `${sectionImageBase}/partners_8.jfif`,
  `${sectionImageBase}/partners_9.jfif`,
  `${sectionImageBase}/partners_10.jfif`,
  `${sectionImageBase}/partners_11.jfif`,
];

function chunkItems(items, size = 2) {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

function buildDisplayRows(sections) {
  const rows = [];
  let imageIndex = 0;

  sections.forEach((section, sectionIndex) => {
    if (section.splitItemRows && section.items?.length) {
      const chunkSize = section.splitItemChunkSize || 2;
      const groups = chunkItems(section.items, chunkSize);
      groups.forEach((group, groupIndex) => {
        rows.push({
          type: 'strengthPair',
          key: `${section.title}-${groupIndex}`,
          step: sectionIndex + 1,
          showHead: groupIndex === 0,
          title: section.title,
          icon: section.icon,
          itemPair: group,
          closingParagraph: groupIndex === groups.length - 1 ? section.closingParagraph : undefined,
          imageIndex,
        });
        imageIndex += 1;
      });
      return;
    }

    rows.push({
      type: 'standard',
      key: section.title,
      step: sectionIndex + 1,
      section,
      imageIndex,
    });
    imageIndex += 1;
  });

  return rows;
}

function StrengthPairCard({ row }) {
  const isTriple = row.itemPair.length === 3;

  return (
    <article
      className={`phase-one-zigzag-content phase-one-zigzag-content-pair${isTriple ? ' phase-one-zigzag-content-triple' : ''}`}
    >
      {row.showHead ? (
        <div className="phase-one-zigzag-content-head">
          <span className="phase-one-step">{String(row.step).padStart(2, '0')}</span>
          <span className="material-symbols-outlined goal-icon">{row.icon}</span>
          <h3>{row.title}</h3>
        </div>
      ) : null}
      <div className="phase-one-strengths-stack">
        {row.itemPair.map((item) => (
          <div key={item.label} className="phase-one-strength-block">
            <h4>{item.label}</h4>
            {item.text ? <p>{item.text}</p> : null}
            {item.bullets?.length > 0 ? (
              <ul className="phase-one-strength-bullets">
                {item.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </div>
      {row.closingParagraph ? (
        <p className="phase-one-strengths-closing">{row.closingParagraph}</p>
      ) : null}
    </article>
  );
}

function StandardSectionCard({ section, step }) {
  const hasItems = section.items?.length > 0;

  return (
    <article
      className={`phase-one-zigzag-content${hasItems ? ' phase-one-zigzag-content-has-items' : ''}`}
    >
      <div className="phase-one-zigzag-content-head">
        <span className="phase-one-step">{String(step).padStart(2, '0')}</span>
        <span className="material-symbols-outlined goal-icon">{section.icon}</span>
        <h3>{section.title}</h3>
      </div>

      {section.paragraphs?.map((para) => (
        <p key={para}>{para}</p>
      ))}
      {!section.paragraphs?.length && section.text ? <p>{section.text}</p> : null}

      {hasItems ? (
        <div className="phase-one-subsections-stack">
          {section.items.map((item, index) => (
            <div key={item.label || `subsection-${index}`} className="phase-one-subsection-block">
              {item.label ? <h4 className="phase-one-subsection-heading">{item.label}</h4> : null}
              {item.paragraphs?.map((para) => (
                <p key={para}>{para}</p>
              ))}
              {item.text ? <p>{item.text}</p> : null}
              {item.bullets?.length > 0 ? (
                <ul className="bullets phase-one-bullets">
                  {item.bullets.map((bullet) => (
                    <li key={bullet}>
                      <span className="material-symbols-outlined check">check_circle</span>
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {item.closingParagraph ? <p>{item.closingParagraph}</p> : null}
            </div>
          ))}
        </div>
      ) : null}

      {!hasItems && section.bullets?.length > 0 && (
        <ul className="bullets phase-one-bullets">
          {section.bullets.map((item) => (
            <li key={item}>
              <span className="material-symbols-outlined check">check_circle</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
      {section.closingParagraph && !hasItems ? <p>{section.closingParagraph}</p> : null}
    </article>
  );
}

function PhaseOnePage() {
  const { t } = useLanguage();
  const displayRows = useMemo(
    () => buildDisplayRows(t.phaseOnePage.sections),
    [t.phaseOnePage.sections]
  );
  const videoEmbedUrl = useMemo(
    () => getYouTubeEmbedUrl(t.phaseOnePage.video?.videoUrl),
    [t.phaseOnePage.video?.videoUrl]
  );
  const fileVideoSrc = t.phaseOnePage.video?.videoSrc || null;

  return (
    <main className="inner-page">
      <section className="phase-one-hero-section">
        <div className="container">
          <Reveal>
            <div className="phase-one-hero-card">
              <div
                className="phase-one-hero-bg"
                style={{ backgroundImage: `url(${heroBackgroundImage})` }}
                aria-hidden="true"
              />
              <span className="eyebrow phase-one-eyebrow">{t.phaseOnePage.eyebrow}</span>
              <h1>{t.phaseOnePage.title}</h1>
              <p>{t.phaseOnePage.subtitle}</p>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="phase-one-hero-video-wrap">
              {t.phaseOnePage.video?.title ? (
                <h2 className="phase-one-hero-video-title">{t.phaseOnePage.video.title}</h2>
              ) : null}
              <div className="phase-one-hero-video-frame">
                {videoEmbedUrl ? (
                  <iframe
                    title={t.phaseOnePage.video.title}
                    src={`${videoEmbedUrl}?rel=0`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                ) : fileVideoSrc ? (
                  <video controls playsInline preload="metadata" src={fileVideoSrc} />
                ) : (
                  <div
                    className="phase-one-video-placeholder"
                    style={{ backgroundImage: `url(${heroBackgroundImage})` }}
                  >
                    <div className="phase-one-video-placeholder-overlay" />
                    <div className="phase-one-video-placeholder-body">
                      <span className="material-symbols-outlined phase-one-video-placeholder-icon">
                        play_circle
                      </span>
                      <span className="phase-one-video-placeholder-label">
                        {t.phaseOnePage.video.placeholder}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section phase-one-zigzag-section">
        <div className="container">
          <div className="phase-one-zigzag-list">
            {displayRows.map((row, i) => (
              <article
                key={row.key}
                className={`phase-one-zigzag-row ${i % 2 === 1 ? 'phase-one-zigzag-row-reverse' : ''}${row.type === 'strengthPair' && !row.showHead ? ' phase-one-zigzag-row-strength-part' : ''}`}
              >
                <Reveal direction={i % 2 === 1 ? 'right' : 'left'}>
                  {row.type === 'strengthPair' ? (
                    <StrengthPairCard row={row} />
                  ) : (
                    <StandardSectionCard section={row.section} step={row.step} />
                  )}
                </Reveal>
                <Reveal direction={i % 2 === 1 ? 'left' : 'right'}>
                  <div
                    className="phase-one-zigzag-image phase-one-zigzag-image-full"
                    style={{
                      backgroundImage: `url(${sectionImages[row.imageIndex % sectionImages.length]})`,
                    }}
                    role="img"
                    aria-label={row.type === 'strengthPair' ? row.title : row.section.title}
                  />
                </Reveal>
              </article>
            ))}
          </div>

          <Reveal delay={80}>
            {t.phaseOnePage.conclusion ? (
              <article className="phase-one-conclusion">
                <div className="phase-one-conclusion-head">
                  {/* <span className="phase-one-step">{t.phaseOnePage.conclusion.step}</span> */}
                  {/* <span className="material-symbols-outlined goal-icon">
                    {t.phaseOnePage.conclusion.icon}
                  </span> */}
                  <h3>{t.phaseOnePage.conclusion.title}</h3>
                </div>
                <div className="phase-one-conclusion-body">
                  {t.phaseOnePage.conclusion.paragraphs.map((para) => (
                    <p key={para}>{para}</p>
                  ))}
                </div>
              </article>
            ) : null}
          </Reveal>
        </div>
      </section>

      <section id="partner-application" className="section audience-application phase-one-application">
        <div className="container">
          <PartnershipApplicationForm app={t.phaseOnePage.partnerApplication} />
        </div>
      </section>
    </main>
  );
}

export default PhaseOnePage;
